/**
 * 全域語言處理模組
 * 負責偵測、儲存瀏覽器語言，並在所有 HTTP 請求中自動附帶語言資訊
 */

const LOCALE_HEADER_NAME = 'X-User-Locale';

// 支援的語言列表（應與 Rails I18n.available_locales 同步）
const SUPPORTED_LOCALES = ['zh-TW', 'zh-CN', 'en', 'ja', 'ko'];
const DEFAULT_LOCALE = 'zh-TW'; // 應與 Rails I18n.default_locale 同步

/**
 * 偵測瀏覽器當前語言
 * @returns {string} 支援的語言代碼或預設語言
 */
function detectBrowserLocale() {
  try {
    const browserLangs = navigator.languages || [navigator.language];

    for (const browserLang of browserLangs) {
      // 精確匹配（如 'zh-TW'）
      if (SUPPORTED_LOCALES.includes(browserLang)) {
        return browserLang;
      }

      // 語言族群匹配（如 'zh' 匹配到 'zh-TW'）
      const langFamily = browserLang.split('-')[0];
      const familyMatch = SUPPORTED_LOCALES.find(locale =>
        locale.split('-')[0] === langFamily
      );
      if (familyMatch) {
        return familyMatch;
      }

      // 處理不同格式（如 'zh_TW' -> 'zh-TW'）
      const normalizedLang = browserLang.replace('_', '-');
      if (SUPPORTED_LOCALES.includes(normalizedLang)) {
        return normalizedLang;
      }
    }

    // 沒有匹配的語言，使用預設語言
    return DEFAULT_LOCALE;
  } catch (error) {
    console.warn('Failed to detect browser locale:', error);
    return DEFAULT_LOCALE;
  }
}

/**
 * 從 Cookie 取得儲存的語言
 * @returns {string|null} 儲存的語言或 null
 */
function getCookieLocale() {
  try {
    const match = document.cookie.match(/user_locale=([^;]+)/);
    const locale = match ? match[1] : null;
    // 確保儲存的語言仍在支援列表中
    return locale && SUPPORTED_LOCALES.includes(locale) ? locale : null;
  } catch (error) {
    console.warn('Failed to read locale from cookie:', error);
    return null;
  }
}

/**
 * 將語言儲存到 Cookie
 * @param {string} locale - 語言代碼
 */
function storeCookie(locale) {
  try {
    if (SUPPORTED_LOCALES.includes(locale)) {
      document.cookie = `user_locale=${locale}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
    }
  } catch (error) {
    console.warn('Failed to store locale to cookie:', error);
  }
}

/**
 * 從 URL 查詢參數取得語言
 * @returns {string|null} URL 中的語言或 null
 */
function getUrlLocale() {
  try {
    const params = new URLSearchParams(window.location.search);
    const urlLocale = params.get('locale');
    // 確保 URL 語言在支援列表中
    return urlLocale && SUPPORTED_LOCALES.includes(urlLocale) ? urlLocale : null;
  } catch (error) {
    console.warn('Failed to read locale from URL:', error);
    return null;
  }
}

/**
 * 取得當前使用者語言
 * 優先順序：URL 參數 > Cookie > 瀏覽器偵測
 * @returns {string} 語言代碼
 */
function getCurrentUserLocale() {
  // 1. 優先使用 URL 參數（與後端保持一致）
  const urlLocale = getUrlLocale();
  if (urlLocale) {
    // 同步更新 Cookie
    storeCookie(urlLocale);
    return urlLocale;
  }

  // 2. 使用 Cookie 儲存的語言
  const cookieLocale = getCookieLocale();
  if (cookieLocale) {
    return cookieLocale;
  }

  // 3. 偵測瀏覽器語言（但不自動寫入 Cookie）
  // 只有用戶手動切換語言時才應該更新 Cookie
  const detected = detectBrowserLocale();
  return detected;
}

/**
 * 設定 fetch 的預設 header 包含語言
 */
function setupFetchHeaders() {
  // 儲存原始的 fetch 函數
  const originalFetch = window.fetch;

  // 重寫 fetch 函數，自動添加語言 header
  window.fetch = function(input, init = {}) {
    const locale = getCurrentUserLocale();

    // 確保 headers 物件存在
    if (!init.headers) {
      init.headers = {};
    }

    // 如果 headers 是 Headers 物件，轉換為普通物件
    if (init.headers instanceof Headers) {
      const headersObj = {};
      init.headers.forEach((value, key) => {
        headersObj[key] = value;
      });
      init.headers = headersObj;
    }

    // 添加語言 header
    init.headers[LOCALE_HEADER_NAME] = locale;

    return originalFetch.call(this, input, init);
  };
}

/**
 * 設定 Turbo 的預設 header 包含語言 - DISABLED (Turbo removed)
 */
function setupTurboHeaders() {
  // Turbo headers disabled as Turbo is removed
}

/**
 * 設定 XMLHttpRequest 的預設 header 包含語言
 */
function setupXHRHeaders() {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
    this._method = method;
    this._url = url;
    return originalOpen.call(this, method, url, async, user, password);
  };

  XMLHttpRequest.prototype.send = function(data) {
    const locale = getCurrentUserLocale();

    // 在發送前設定語言 header
    if (!this.getResponseHeader(LOCALE_HEADER_NAME)) {
      this.setRequestHeader(LOCALE_HEADER_NAME, locale);
    }

    return originalSend.call(this, data);
  };
}

/**
 * 設定 Rails UJS 的語言 header
 */
function setupRailsHeaders() {
  // 為 Rails forms 設定預設 header
  document.addEventListener('ajax:beforeSend', (event) => {
    const locale = getCurrentUserLocale();
    const xhr = event.detail[0];
    xhr.setRequestHeader(LOCALE_HEADER_NAME, locale);
  });
}

/**
 * 初始化全域語言處理
 */
function initializeGlobalLocale() {
  // 確保語言已被偵測
  const currentLocale = getCurrentUserLocale();

  // 確保 Cookie 存在（如果只有瀏覽器偵測的語言，則寫入 Cookie）
  const cookieLocale = getCookieLocale();
  if (!cookieLocale && currentLocale) {
    storeCookie(currentLocale);
  }

  // 不需要重定向！Backend 的 session 會自動處理 locale 持久化
  // Priority: URL param > session > user.locale > cookie > X-User-Locale header > browser

  // 設定各種 HTTP 請求的預設 header（用於 AJAX 請求）
  setupFetchHeaders();
  setupTurboHeaders();
  setupXHRHeaders();
  setupRailsHeaders();

  console.log(`Global locale initialized: ${currentLocale}`);
}

/**
 * 手動更新語言（供外部調用）
 * @param {string} locale - 新的語言代碼
 */
function updateLocale(locale) {
  if (locale && SUPPORTED_LOCALES.includes(locale) && locale !== getCookieLocale()) {
    storeCookie(locale);
    console.log(`Locale manually updated to: ${locale}`);
  }
}

/**
 * 取得支援的語言列表
 * @returns {Array<string>} 支援的語言代碼列表
 */
function getSupportedLocales() {
  return [...SUPPORTED_LOCALES];
}

/**
 * 檢查語言是否被支援
 * @param {string} locale - 語言代碼
 * @returns {boolean} 是否支援
 */
function isSupportedLocale(locale) {
  return SUPPORTED_LOCALES.includes(locale);
}

// 導出函數供外部使用
export {
  initializeGlobalLocale,
  getCurrentUserLocale,
  updateLocale,
  getSupportedLocales,
  isSupportedLocale,
  LOCALE_HEADER_NAME,
  DEFAULT_LOCALE
};
